import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import Moment from 'react-moment';
import moment from 'moment';
import { Link } from 'react-router';

import { makeGetTask, makeIsDependant, makeGetFilteredTask } from '../selectors/tasks';
import { makeGetMilestone } from '../selectors/milestones';
import { makeGetTaskNotifications } from '../selectors/notifications';
import { makeGetProject } from '../selectors/projects';

import { fetchTask, updateTask, assignTask, unassignTask } from '../actions/tasks';
import { setCustomFieldValue } from '../actions/custom-fields';

import { userRoles } from '../constants';
import { setEndOfContenteditable, getUserFriendlyRoles, isTempId } from '../utils/helpers';
import { canUser } from '../services/permissions-service';
import { createAlert } from '../services/alert-service';
import { trackEvent } from '../services/events-tracker-service';

import TaskCompleteToggle from 'components/TaskCompleteToggle';
import CanUser from './can-user';
import TaskActions from './task-actions';
import MilestoneName from './milestone-name';
import ProjectName from './project-name';
import UsersList from './users-list';
import LabelsList from './labels-list';
import Tooltip from './tooltip';
import NiftyMarkdown from './nifty-markdown';
import FeatureAvailable from './feature-available';
import CustomFields from './custom-fields';
import SelectMilestonePopout from './select-milestone-popout';
import LabelPicker from './label-picker';
import DateElement from './date-element';

import IconRoadmap from '../icons/icon-roadmap';
import IconSubtask from '../icons/icon-subtask';
import IconFile from '../icons/icon-file';
import IconDiscussionAlt from '../icons/icon-discussion-alt';
import IconEye from '../icons/icon-eye';
import IconCalendarSimple from '../icons/icon-calendar-simple';
import IconLock from '../icons/icon-lock';
import IconRepeat from '../icons/icon-repeat';
import IconArrowDown from '../icons/icon-arrow-down';
import IconProject from '../icons/icon-project';

class Task extends PureComponent {
    static defaultProps = {
        showActions: true
    };

    state = {
        editing: false,
        name: this.props.task ? this.props.task.name : ''
    }

    handleTaskClick = () => {
        if (!this.props.id || this.props.isTemp) {
            return;
        }

        this.props.onClick && this.props.onClick(this.props.task.nice_id || this.props.id);
    }

    handleMilestoneClick = () => {
        if (!this.props.task.milestone || !this.props.onMilestoneClick) {
            return;
        }

        this.props.onMilestoneClick(this.props.task.milestone);
    }

    handleEditNameClick = () => {
        this.setState({
            editing: true
        }, () => {
            this.nameElement && this.nameElement.focus();
            this.nameElement && setEndOfContenteditable(this.nameElement)
        });
    }

    handleNameChange = () => {
        this.setState({
            name: this.nameElement.textContent
        });
    }

    handleNameBlur = () => {
        if (!this.nameElement.textContent) {
            this.setState({
                name: this.props.task.name
            });

            return;
        }

        updateTask(this.props.id, { name: this.nameElement.textContent });
    }

    handleNameKeyDown = event => {
        if (event.which === 13) {
            this.nameElement.blur();
        }
    }

    handleAssigneeRemove = userId => {
        unassignTask(this.props.id, [userId]);
    }

    handleAssigneeAdd = user => {
        assignTask(this.props.id, [user.id || user]);
    }

    handleTaskCompleteToggle = async (completed, taskGroupId) => {
        if (this.props.isTemp) {
            return;
        }

        try {
            this.updateTask({
                completed,
                task_group: taskGroupId
            });

            if (completed) {
                trackEvent('TASK.COMPLETE');
            }
        } catch (e) {
            // es lint
        }
    }

    handleTaskMoveClick = () => {
        if (this.props.task.archived || !this.taskMoveToProjectCtrl) {
            return;
        }

        this.taskMoveToProjectCtrl.show();
    }

    handleMoveToProjectConfirm = (taskGroupId, projectId) => {
        this.moveToTaskGroup(taskGroupId, projectId);
    }

    handleMilestoneSelect = milestone => {
        if (milestone && this.props.task.milestone === milestone.id) {
            return;
        }

        this.updateTask({
            milestone: milestone.id
        });
    }

    handleMilestoneUntie = () => {
        if (!this.props.task.milestone) {
            return;
        }

        this.updateTask({
            milestone: null
        });
    }

    handleTaskSelect = task => {
        if (task && this.props.task.dependency === task.id) {
            return;
        }

        this.updateTask({
            dependency: task.id
        });
    }

    handleTaskUntie = () => {
        if (!this.props.task.dependency) {
            return;
        }

        this.updateTask({
            dependency: null
        });
    }

    handleCustomFieldChange = (id, value) => {
        if (this.props.isTemp) {
            return;
        }

        setCustomFieldValue('task', this.props.id, id, value);
    }

    handleSetDeadlineClick = () => {
        this.dueDateCtrl && this.dueDateCtrl.show();
    }

    handleDueDateChange = nextDate => {
        const nextDateFormatted = nextDate && moment(nextDate).format();

        if (this.props.task.due_date === nextDateFormatted) {
            return;
        }

        if (this.props.milestone) {
            if (new Date(this.props.milestone.start) > nextDate) {
                createAlert('default', 'Task tied to milestone cannot end earlier than milestone start date');
                return;
            }

            if (new Date(this.props.milestone.end) < nextDate) {
                createAlert('default', 'Task tied to a milestone cannot end after the milestone end date.');
                return;
            }
        }

        this.updateTask({
            due_date: nextDateFormatted
        });
    }

    handleDueDateRemove = () => {
        this.updateTask({
            due_date: null
        });

        this.dueDateCtrl && this.dueDateCtrl.hide();
    }

    moveToTaskGroup(taskGroupId, projectId) {
        if (this.props.isTemp) {
            return;
        }

        const props = {
            task_group: taskGroupId
        }

        if (projectId && projectId !== this.props.task.project) {
            props.project = projectId;
            props.labels = [];
            props.assignees = [];
            props.milestone = null;
        }

        this.updateTask(props)
            .then(() => {
                createAlert('success', '<i class="icon material-icons">check_circle</i>  Successfully moved task.');
            });
    }

    updateTask(taskProps, preventSave = false) {
        return updateTask(this.props.id, taskProps, preventSave);
    }

    shake() {
        this.setState({
            shaking: true
        });

        setTimeout(() => {
            this.setState({
                shaking: false
            });
        }, 300);
    }

    fetchTask() {
        fetchTask(this.props.id);
    }

    attemptFetch() {
        if (this.props.id && !this.props.task && !this.props.filtered) {
            this.fetchTask();
        }
    }

    setDueDateCtrl = ctrl => this.dueDateCtrl = ctrl;

    get className() {
        const className = ['task'];
        if (this.props.isDragging) className.push('is-dragging');
        if (this.props.current) className.push('active');
        if (this.props.isOver) className.push('is-over');
        if (this.props.task.archived) className.push('archived');
        if (this.props.task.completed) className.push('completed');
        if (this.state.shaking) className.push('shake');
        if (this.props.draggedTask === this.props.id) className.push('dragged-task');
        if (this.props.simpleTask) className.push('simple-task');
        if (this.props.dependantOn) className.push('dependant');

        return className.join(' ');
    }

    get assigneesElement() {
        if (!this.props.isTemp && ((this.props.task.assignees && this.props.task.assignees.length) || canUser('assignToTask', [this.props.task.created_by]))) {
            return (
                <div className="meta-item">
                    <UsersList
                        popoutTitle="ASSIGN TASK TO:"
                        projectId={this.props.task.project}
                        editable={!this.props.task.completed && !this.props.task.archived && canUser('assignToTask', [this.props.task.created_by])}
                        size={this.props.viewType === 'table' ? 'small' : 'xsmall'}
                        border={true}
                        users={this.props.task.assignees}
                        roles={userRoles()}
                        maxUsersCount={2}
                        popoutSize="xxsmall"
                        subtitles={canUser('unassignFromTask', [this.props.task.created_by]) ? 'Un-assign user' : null}
                        emailMatchText="Select user role above and click “Invite” below and we’ll shoot this person an email notifying them that they’ve been added to this task."
                        noMatchText="Looks like that person isn’t a member yet. Enter their email address to add them to the task and project."
                        closeOnEveryClick={true}
                        onSubtitleClick={this.handleAssigneeRemove}
                        excludedUsers={this.props.task.assignees}
                        onUserSelect={this.handleAssigneeAdd}
                        addUserTooltip="Add assignee"
                    />
                </div>
            );
        }

        return null;
    }

    get labelsElement() {
        const { labelsFetched, task } = this.props;
        if (!labelsFetched || !task || !task.labels || !task.labels.length || task.archived || task.completed) {
            return null;
        }

        return (
            <div className="task-labels">
                <LabelsList labels={task.labels} inverted={true} />
            </div>
        );
    }

    get taskIDElement() {
        return (
            <span className="task-id-holder">
                <Tooltip tooltip="Reference this task in discussions and messages." placement="top">
                    <span className="task-id">{ this.props.task.nice_id }</span>
                </Tooltip>
            </span>
        )
    }

    componentDidMount() {
        this.attemptFetch();
    }

    componentDidUpdate(prevProps) {
        this.props.provideController && this.props.provideController({
            shake: () => {
                this.shake();
            }
        });

        this.attemptFetch();

        if (this.props.task && this.props.task.name !== this.state.name) {
            this.setState({
                name: this.props.task.name
            });
        }
    }

    componentWillUnmount() {
        this.props.provideController && this.props.provideController(null);
    }

    get innerElement() {
        const { task } = this.props;
        const disabledModules = (this.props.project?.disabled_modules || []);

        if (this.props.viewType === 'table') {
            return (
                <React.Fragment>
                    <div className="task-cell task-cell-large">
                        <div className="task-overlay" onClick={this.handleTaskClick} />

                        <div className="task-cell-inner" data-notification={this.props.notifications.length}>
                            {
                                !this.props.public ? (
                                    <TaskCompleteToggle
                                        dependantOn={this.props.dependantOn}
                                        completed={task.completed}
                                        disabled={this.props.isTemp}
                                        projectId={task.project}
                                        onChange={this.handleTaskCompleteToggle}
                                    />
                                ) : null
                            }

                            <h5>
                                { this.props.name || task.name }
                            </h5>

                            {
                                task.rule ?
                                    <Tooltip tooltip="This task is recurring">
                                        <div className="meta-item with-tooltip">
                                            <span className="meta-icon meta-icon-single">
                                                <IconRepeat size="12px" />
                                            </span>
                                        </div>
                                    </Tooltip>
                                    : null
                            }

                            {
                                this.props.task?.hidden?.length ?
                                    <Tooltip tooltip={`This task is hidden from ${getUserFriendlyRoles(this.props.task.hidden, userRoles())}`}>
                                        <div className="meta-item with-tooltip">
                                            <span className="meta-icon meta-icon-single">
                                                <IconEye />
                                            </span>
                                        </div>
                                    </Tooltip>
                                    : null
                            }

                            {
                                this.props.task.nice_id ? this.taskIDElement : null
                            }
                        </div>
                    </div>

                    {
                        this.props.showProjectColumn && !this.props.public ? (
                            <div className="task-cell">
                                <div className="task-cell-inner">
                                    <Link to={`/${task.project}`}>
                                        <ProjectName id={task.project} />
                                    </Link>
                                </div>
                            </div>
                        ) : null
                    }

                    {
                        !this.props.public ? (
                            <div className="task-cell">
                                <div className="task-cell-inner">
                                    { this.assigneesElement }
                                </div>
                            </div>
                        ) : null
                    }

                    <div className={'task-cell task-cell-small' + (canUser('editTask', [task.created_by]) ? ' actionable' : '')} onClick={this.handleSetDeadlineClick}>
                        <div className="task-cell-inner">
                            {
                                task.due_date ? <span className={(moment().diff(task.due_date) > 0 && !task.completed ? 'text-warning' : '')}><Moment format="MMM D">{task.due_date}</Moment></span> : null
                            }

                            {
                                !this.props.draggedTask && !task.archived && !task.completed ? (
                                    <CanUser type="editTask" customList={[task.created_by]}>
                                        {
                                            !task.due_date ? <span className="placeholder">Add</span> : null
                                        }

                                        <DateElement
                                            selected={task.due_date}
                                            title={task.due_date ? 'CHANGE DUE DATE' : 'ADD A DUE DATE'}
                                            xAlign="right"
                                            defaultXPosition="left"
                                            removePopoutTitle="REMOVE DUE DATE"
                                            removeBodyText="Are you sure you want to remove the due date from this task?"
                                            minDate={new Date()}
                                            showTimeSelect={true}
                                            showQuickOptions={true}
                                            onChange={this.handleDueDateChange}
                                            onRemove={this.handleDueDateRemove}
                                            provideController={this.setDueDateCtrl}
                                        />
                                    </CanUser>
                                ) : null
                            }

                        </div>
                    </div>

                    <div className={'task-cell task-cell-no-pad' + (canUser('editTask', [this.props.task.created_by]) ? ' actionable' : '')}>
                        <div className="task-cell-inner">
                            {
                                !this.props.draggedTask && !task.archived && !task.completed ? (
                                    <CanUser type="editTask" customList={[task.created_by]}>
                                        <LabelPicker
                                            taskId={this.props.id}
                                            projectId={task.project}
                                            taskLabels={task.labels || []}
                                            xAlign="left"
                                            defaultXPosition="right"
                                        >
                                            { this.labelsElement || <span className="placeholder">Add</span> }
                                        </LabelPicker>
                                    </CanUser>
                                ) : this.labelsElement
                            }

                            <CanUser type="editTask" customList={[task.created_by]} reverse={true}>
                                { this.labelsElement }
                            </CanUser>
                        </div>
                    </div>

                    {
                        !this.props.public && (this.props.showAllColumns || !this.props.project || !disabledModules.includes('milestones')) ? (
                            <div className="task-cell">
                                <div className="task-cell-inner">
                                    {
                                        disabledModules.includes('milestones') ? 'N/A' : (
                                            <React.Fragment>
                                                <span className={this.props.task.milestone && this.props.onMilestoneClick ? 'task-item-action' : ''} onClick={this.handleMilestoneClick}>
                                                    <MilestoneName id={task.milestone} />
                                                </span>

                                                {
                                                    !this.props.draggedTask && !task.archived && !task.completed ? (
                                                        <CanUser type="editTask" customList={[task.created_by]}>
                                                            {
                                                                task.milestone ? (
                                                                    <span className="task-cell-arrow">
                                                                        <IconArrowDown size="10px" />
                                                                    </span>
                                                                ) : (
                                                                    <span className="placeholder">
                                                                        Tie
                                                                    </span>
                                                                )
                                                            }

                                                            <SelectMilestonePopout
                                                                title="TIE TASK TO A MILESTONE"
                                                                projectId={task.project}
                                                                selected={task.milestone}
                                                                onSelect={this.handleMilestoneSelect}
                                                                onRemove={this.handleMilestoneUntie}
                                                                showCreate={true}
                                                                removeCopy="Untie Milestone"
                                                                xAlign="right"
                                                                defaultXPosition="left"
                                                            >
                                                                <div className="popout-toggle" />
                                                            </SelectMilestonePopout>
                                                        </CanUser>
                                                    ) : null
                                                }
                                            </React.Fragment>
                                         )
                                    }
                                </div>
                            </div>
                        ) : null
                    }

                    {
                        !this.props.public && this.props.id && !this.props.isTemp && this.props.showCustomFields && !this.props.draggedTask ? (
                            <FeatureAvailable type="customFields">
                                <CustomFields
                                    projectId={task.project}
                                    type="row-single"
                                    association="task"
                                    associationId={this.props.id}
                                    values={task.fields}
                                    noContainers={true}
                                    onChange={this.handleCustomFieldChange}
                                />
                            </FeatureAvailable>
                        ) : null
                    }
                </React.Fragment>
            )
        }

        return (
            <div className="task-inner" data-notification={this.props.notifications.length}>
                <div className="task-overlay" onClick={this.handleTaskClick} />

                {
                    this.props.showActions ? (
                        <CanUser type="editTask">
                            <TaskActions
                                id={this.props.id}
                                milestone={this.props.milestone}
                                projectId={task.project}
                                onEditNameClick={this.handleEditNameClick}
                            />
                        </CanUser>
                    ) : null
                }

                <div className="task-top">
                    {
                        this.props.task.nice_id ? this.taskIDElement : null
                    }

                    { this.labelsElement }
                </div>
                

                <div className="task-name">
                    <CanUser type="editTask" customList={[task.created_by]}>
                        <div className="task-complete-toggle-holder">
                            <TaskCompleteToggle
                                dependantOn={this.props.dependantOn}
                                completed={task.completed}
                                disabled={this.props.isTemp}
                                projectId={task.project}
                                onChange={this.handleTaskCompleteToggle}
                            />
                        </div>
                    </CanUser>

                    <span className="task-name-text"                    
                        ref={element => {this.nameElement = element}}
                        contentEditable={this.state.editing}
                        placeholder="Add task name…"
                        dangerouslySetInnerHTML={{__html: task && task.name}}
                        onInput={this.handleNameChange}
                        onBlur={this.handleNameBlur}
                        onKeyDown={this.handleNameKeyDown}
                    />
                </div>

                {
                    !this.props.simpleTask && task.banner ? (
                        <div className="task-banner">
                            <img src={task.banner} alt="Task banner" />
                        </div>
                    ) : null
                }

                {
                    task.description ? (
                        <div className="task-description">
                            <NiftyMarkdown unstyled={true}>{task.description}</NiftyMarkdown>
                        </div>
                    ) : null
                }

                <div className="task-foot">
                    <div className="meta">
                        {
                            !this.props.public && this.props.showProject ? (
                                <div className="meta-item">
                                    <span className="meta-icon">
                                        <IconProject />
                                    </span>

                                    <ProjectName id={task.project} />
                                </div>
                            ) : !this.props.public && task.milestone && !disabledModules.includes('milestones') ? (
                                <div className="meta-item">
                                    <span className="meta-icon">
                                        <IconRoadmap />
                                    </span>

                                    <MilestoneName id={task.milestone} />
                                </div>
                            ) : null
                        }

                        {
                            task.due_date ?
                                <div className={'meta-item' + (moment().diff(task.due_date) > 0 && !task.completed ? ' text-warning' : '')}>
                                    <span className="meta-icon">
                                        <IconCalendarSimple />
                                    </span>

                                    <Moment format="MMM D">{task.due_date}</Moment>
                                </div>
                                : null
                        }

                        {
                            task.total_subtasks ? (
                                <div className={'meta-item' + (task.total_subtasks === task.completed_subtasks ? ' active' : '')}>
                                    <span className="meta-icon">
                                        <IconSubtask />
                                    </span>

                                    <span>{task.completed_subtasks}/{task.total_subtasks}</span>
                                </div>
                            ) : null
                        }

                        {
                            task.comments ?
                                <div className="meta-item">
                                    <span className="meta-icon" style={{marginRight: '2px'}}>
                                        <IconDiscussionAlt />
                                    </span>

                                    {task.comments}
                                </div>
                                : null
                        }

                        {
                            task.attachments && task.attachments.length ?
                                <div className="meta-item">
                                    <span className="meta-icon">
                                        <IconFile />
                                    </span>

                                    {task.attachments.length}
                                </div>
                                : null
                        }

                        {
                            this.props.task?.hidden?.length ?
                                <Tooltip tooltip={`This task is hidden from ${getUserFriendlyRoles(this.props.task.hidden, userRoles())}`}>
                                    <div className="meta-item with-tooltip">
                                        <span className="meta-icon meta-icon-single">
                                            <IconEye />
                                        </span>
                                    </div>
                                </Tooltip>
                                : null
                        }

                        {
                            task.rule ?
                                <Tooltip tooltip="This task is recurring">
                                    <div className="meta-item with-tooltip">
                                        <span className="meta-icon meta-icon-single">
                                            <IconRepeat size="12px" />
                                        </span>
                                    </div>
                                </Tooltip>
                                : null
                        }
                    </div>

                    { this.assigneesElement }
                </div>
            </div>
        )
    }

    render() {
        if (!this.props.task) {
            return null;
        }

        if (this.props.withoutContainer) {
            return this.innerElement;
        }

        return (
            <div className={this.className}>
                { this.innerElement }
            </div>
        );
    };
}

const makeMapStateToProps = () => {
    const getTask = makeGetTask();
    const getMilestone = makeGetMilestone();
    const isDependant = makeIsDependant();
    const getTaskNotifications = makeGetTaskNotifications();
    const getFilteredTask = makeGetFilteredTask();
    const getProject = makeGetProject();

    return ({ projects, tasks, notifications, milestones }, ownProps) => {
        const task = ownProps.id && getTask(tasks.items, ownProps.id);
        const filteredTask = task && getFilteredTask(task, tasks.filter);
        const project = task && getProject(projects.items, task.project);

        return {
            task: filteredTask,
            milestone: project && !(project.disabled_modules || []).includes('milestones') && task.milestone && getMilestone(milestones.items, task.milestone),
            isTemp: isTempId(ownProps.id),
            project,
            dependantOn: isDependant(ownProps.id, true, tasks.items, milestones.items),
            filtered: !filteredTask && task,
            notifications: ownProps.id ? getTaskNotifications(notifications.items, ownProps.id) : []
        }
    }
}

export default connect(makeMapStateToProps)(Task);
